import { Breadcrumb, Button, Flex, Form, Input, Select, Switch } from 'antd';
import type { FC } from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetIntegrationInfo } from '@modules/integrations/hooks/use-get-integration-info';
import { useGetChannelInfoChannelInfoGetQuery } from '@shared/api';
import { FetchingErrorState } from '@shared/ui/fetching-error-state/fetching-error-state';
import { LoadingState } from '@shared/ui/loading-state/loading-state';
import { ScrollContainer } from '@shared/ui/scroll-container';
import { Text } from '@shared/ui/antd';
import { useFormSchemaCreate } from '@modules/integrations/hooks/use-form-schema-create';
import type { WebhookIntegrationCreateFormValues } from '@modules/integrations/lib/types';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { INTEGRATIONS_MAX_FORM_LIMIT } from '@modules/integrations/lib/constants';
import { useUpdateIntegration } from '@modules/integrations/hooks/use-update-integration';
import { useCreateIntegration } from '@modules/integrations/hooks/use-create-integration';
import { Routes, buildOrganizationRoute } from '@shared/constants/routes';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { IntegrationsItemFields } from '../integrations/ui/integrations-item/ui/integrations-item-fields';
import styles from './integration-details.module.scss';

interface IntegrationDetailsProps {
  integrationId?: number;
  channelId: number;
  isCreateMode?: boolean;
}

export const IntegrationDetails: FC<IntegrationDetailsProps> = ({
  integrationId,
  channelId,
  isCreateMode = false,
}) => {
  const navigate = useNavigate();
  const { organizationId } = useParams<{ organizationId?: string }>();
  const { t: tIntegrations } = useTranslation('translation', { keyPrefix: 'integrations' });
  const { t: tChannels } = useTranslation('translation', { keyPrefix: 'channels' });
  const normalizedOrganizationId =
    organizationId && Number.isFinite(Number(organizationId)) ? Number(organizationId) : null;
  const channelsRoute = normalizedOrganizationId
    ? buildOrganizationRoute(Routes.organizationScope.channels, normalizedOrganizationId)
    : Routes.channels.root;
  const channelUpdateRoute = normalizedOrganizationId
    ? buildOrganizationRoute(Routes.organizationScope.channelsUpdate, normalizedOrganizationId, {
        channelId,
      })
    : generatePath(Routes.channels.update, {
        channelId,
      });
  const channelIntegrationsRoute = normalizedOrganizationId
    ? buildOrganizationRoute(
        Routes.organizationScope.channelsIntegrations,
        normalizedOrganizationId,
        {
          channelId,
        },
      )
    : generatePath(Routes.channels.integrations, { channelId });
  const isExistingIntegration = Boolean(
    integrationId && Number.isFinite(integrationId) && !isCreateMode,
  );
  const normalizedIntegrationId = integrationId ?? 0;

  const { integrationInfo, isLoading, isError, refetch } = useGetIntegrationInfo(
    normalizedIntegrationId,
    isExistingIntegration,
  );
  const { data: channelInfo } = useGetChannelInfoChannelInfoGetQuery(
    { channelId },
    { skip: !channelId, refetchOnMountOrArgChange: true },
  );
  const { baseFields } = useFormSchemaCreate();
  const { updateIntegration, isLoading: isUpdating } = useUpdateIntegration();
  const { createIntegration, isCreateIntegrationLoading } = useCreateIntegration();

  const {
    handleSubmit,
    control,
    reset,
    clearErrors,
    formState: { errors, isSubmitting, isValid, isDirty },
  } = useForm<WebhookIntegrationCreateFormValues>({
    mode: 'onTouched',
    resolver: zodResolver(baseFields),
    defaultValues: {
      title: '',
      callbackUrl: '',
      method: 'POST',
    },
  });
  const requestMethod = useWatch({ control, name: 'method' });

  useEffect(() => {
    if (!isExistingIntegration) {
      reset({
        title: '',
        callbackUrl: '',
        method: 'POST',
      });
      return;
    }

    if (integrationInfo) {
      reset({
        title: integrationInfo.title ?? '',
        callbackUrl: integrationInfo.callback_url ?? '',
        method: integrationInfo.method ?? 'POST',
      });
    }
  }, [integrationInfo, isExistingIntegration, reset]);

  const handleSubmitForm = async (values: WebhookIntegrationCreateFormValues) => {
    if (isExistingIntegration && integrationId) {
      await updateIntegration(integrationId, values);
      reset(values);
      return;
    }

    const created = await createIntegration(channelId, values);
    if (!created?.id) return;

    navigate(`${channelIntegrationsRoute}?integrationId=${created.id}`, {
      replace: true,
    });
  };

  if (isExistingIntegration && isLoading) {
    return (
      <Flex vertical gap={20} className={styles.root}>
        <LoadingState />
      </Flex>
    );
  }

  if (isExistingIntegration && (isError || !integrationInfo)) {
    return (
      <Flex vertical gap={20} className={styles.root}>
        <FetchingErrorState refetch={refetch} />
      </Flex>
    );
  }

  const isFormLoading = isSubmitting || isUpdating || isCreateIntegrationLoading;
  const channelBreadcrumbKey = channelInfo?.type ? `breadcrumbs.${channelInfo.type}` : null;

  return (
    <Flex vertical gap={20} className={styles.root}>
      <Breadcrumb
        items={[
          {
            title: tChannels('channels'),
            href: channelsRoute,
          },
          {
            title: channelBreadcrumbKey ? tChannels(channelBreadcrumbKey) : tChannels('channels'),
            href: channelUpdateRoute,
          },
          {
            title: tIntegrations('emptyWebhookTitle'),
          },
        ]}
      />

      <ScrollContainer>
        <Flex vertical gap={16} className={styles.container}>
          <Form
            onChange={() => clearErrors('root')}
            onFinish={handleSubmit(handleSubmitForm)}
            className={styles.form}
            layout="vertical"
          >
            <Flex vertical className={styles.section}>
              <Flex vertical className={styles.sectionHeader}>
                <Text variant="md" fontWeight={600}>
                  {tIntegrations('details.title')}
                </Text>
                <Text variant="xs" className={styles.sectionDescription}>
                  {tIntegrations('details.titleHint')}
                </Text>
              </Flex>
              <Form.Item
                name="title"
                help={errors.title?.message}
                validateStatus={errors.title ? 'error' : ''}
              >
                <Controller
                  control={control}
                  name="title"
                  render={({ field }) => (
                    <Input
                      {...field}
                      showCount
                      maxLength={INTEGRATIONS_MAX_FORM_LIMIT.title}
                      disabled={isFormLoading}
                      placeholder={tIntegrations('namePlaceholder')}
                      className={styles.input}
                    />
                  )}
                />
              </Form.Item>
            </Flex>

            <Flex vertical className={styles.section}>
              <Flex
                className={styles.callbackHeader}
                justify="space-between"
                align="flex-start"
                gap={16}
              >
                <Flex vertical className={styles.sectionHeader}>
                  <Text variant="md" fontWeight={600}>
                    {tIntegrations('details.callbackUrl')}
                  </Text>
                  <Text variant="xs" className={styles.sectionDescription}>
                    {tIntegrations(`details.callbackUrlHint.${requestMethod}`)}
                  </Text>
                </Flex>
                <Controller
                  control={control}
                  name="method"
                  render={({ field }) => (
                    <Select
                      className={styles.methodSelector}
                      options={[
                        { label: 'GET', value: 'GET' },
                        { label: 'POST', value: 'POST' },
                      ]}
                      value={field.value}
                      disabled={isFormLoading}
                      aria-label={tIntegrations('details.methodLabel')}
                      onChange={field.onChange}
                    />
                  )}
                />
              </Flex>
              <Form.Item
                name="callbackUrl"
                help={errors.callbackUrl?.message}
                validateStatus={errors.callbackUrl ? 'error' : ''}
              >
                <Controller
                  control={control}
                  name="callbackUrl"
                  render={({ field }) => (
                    <Input
                      {...field}
                      showCount
                      maxLength={INTEGRATIONS_MAX_FORM_LIMIT.callbackUrl}
                      disabled={isFormLoading}
                      placeholder={tIntegrations('callbackUrlPlaceholder')}
                      className={styles.input}
                    />
                  )}
                />
              </Form.Item>
            </Flex>

            <Flex vertical className={styles.section}>
              <Flex vertical className={styles.sectionHeader}>
                <Text variant="md" fontWeight={600}>
                  {tIntegrations('details.status')}
                </Text>
                <Text variant="xs" className={styles.sectionDescription}>
                  {tIntegrations('details.statusHint')}
                </Text>
              </Flex>
              <Flex className={styles.switchRow} align="center" gap={12}>
                <Switch checked disabled />
                <Text variant="sm">{tIntegrations('activeStatus')}</Text>
              </Flex>
            </Flex>

            {isExistingIntegration && (
              <Flex vertical className={styles.section}>
                <Flex vertical className={styles.sectionHeader}>
                  <Text variant="md" fontWeight={600}>
                    {tIntegrations('details.mappingTitle')}
                  </Text>
                  <Text variant="xs" className={styles.sectionDescription}>
                    {tIntegrations('details.mappingHint')}
                  </Text>
                </Flex>
                <IntegrationsItemFields
                  integrationId={normalizedIntegrationId}
                  isOpen
                  requestMethod={requestMethod}
                />
              </Flex>
            )}

            <Flex className={styles.actions}>
              <Form.Item style={{ marginBottom: 0 }}>
                <Button
                  className={styles.saveButton}
                  type="primary"
                  htmlType="submit"
                  loading={isFormLoading}
                  disabled={!isValid || isFormLoading || !isDirty}
                >
                  {tIntegrations('save')}
                </Button>
              </Form.Item>
            </Flex>
          </Form>
        </Flex>
      </ScrollContainer>
    </Flex>
  );
};
